import React, { FC } from 'react';
import cn from 'classnames';
import { css } from 'styled-components';

import { Star, ShoppingCart as ShoppingCartFilled } from '@crehana/icons/fill';
import { BarChart2, ShoppingCart } from '@crehana/icons/outline';
import { useTranslation } from '@crehana/i18n';
import BookmarkButton from '@crehana/web/dist/components/Cards/components/BookmarkButton';
import { LazyImage, Clamp, PrimaryButton, InverseButton } from '@crehana/ui';

import CourseTag from '@crehana/web/dist/components/Tags/CourseTag';

import { useMainMenuContext } from '@crehana/web/dist/MainMenu/hooks';

import { SearchCourse } from '../types';

type SearchCourseCardProps = SearchCourse & {
  className?: string;
  isDark: boolean;
  loadingCart: boolean;
  isInShoppingCart: boolean;
  isSavedCourse: boolean;
  source?: string;
  sourceDetail?: string;
  addCourseToCart: (course: { courseId: number }) => void;
  removeCourseFromCart: (course: { courseId: number }) => void;
};

const SearchCourseCard: FC<SearchCourseCardProps> = ({
  className,
  isDark,
  originalId,
  title,
  promoImage,
  url,
  courseType,
  courseTag,
  teacherName,
  score,
  level,
  price,
  loadingCart,
  isInShoppingCart,
  isSavedCourse,
  source,
  sourceDetail,
  addCourseToCart,
  removeCourseFromCart,
}) => {
  const { t } = useTranslation();
  const { me } = useMainMenuContext();

  const renderButtons = () => {
    if (!me.isAnonymous && !me.isPAYG) {
      return (
        <PrimaryButton
          className="mt-16"
          label={t('GO_TO_COURSE_DETAIL')}
          href={url}
          size="s"
          isDark={isDark}
          full
        />
      );
    }
    const shoppingCartButtonBaseProps = {
      className: 'w-40 flex-shrink-0 mr-8 sm:mr-12',
      size: 's' as 's',
      isDark,
      disabled: loadingCart,
    };

    return (
      <div className="flex">
        {isInShoppingCart ? (
          <PrimaryButton
            {...shoppingCartButtonBaseProps}
            isGray
            leftIcon={{ component: ShoppingCartFilled }}
            onClick={() =>
              removeCourseFromCart({ courseId: Number(originalId) })
            }
          />
        ) : (
          <InverseButton
            {...shoppingCartButtonBaseProps}
            leftIcon={{ component: ShoppingCart }}
            onClick={() => addCourseToCart({ courseId: Number(originalId) })}
          />
        )}
        <PrimaryButton
          className="px-0"
          label={{
            xs: t('GO_TO_COURSE_DETAIL'),
            sm: t('GO_TO_COURSE_DETAIL_SHORT'),
            lg: t('GO_TO_COURSE_DETAIL'),
          }}
          href={url}
          size="s"
          isDark={isDark}
          full
        />
      </div>
    );
  };

  return (
    <article
      className={cn(
        'h-full flex flex-col rounded-5 overflow-hidden',
        isDark ? 'bg-base-lighter-dark-mode' : 'bg-white shadow-1-dp',
        className,
      )}
    >
      <div
        className="relative flex-shrink-0 w-full"
        css={`
          padding-top: 62%;
        `}
      >
        <LazyImage
          className="absolute inset-0 w-full h-full"
          imgClassName="object-cover"
          alt={title || ''}
          src={promoImage?.url || ''}
          placeholder={promoImage?.placeholder}
          isDark={isDark}
        />
        <div
          className="absolute inset-0 flex flex-col items-start w-full h-full p-8"
          css={css`
            background-image: linear-gradient(
              180deg,
              rgba(24, 27, 50, 0) 52.08%,
              ${props => props.theme.colors.base.main} 100%
            );
          `}
        >
          <CourseTag courseTag={courseTag} courseType={courseType} />
          <div className="flex items-center justify-between w-full mt-auto text-white font-subtitle4">
            <div className="flex items-center">
              <Star
                size={16}
                ignoreColor
                className="mr-4 fill-current text-yellow-main"
              />
              {score}
            </div>
            {level && (
              <div className="flex items-center">
                <BarChart2
                  size={16}
                  ignoreColor
                  className="mr-4 text-white fill-current"
                />
                {level}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start justify-between h-full p-12">
        <div className="flex flex-col w-full">
          <div className="flex items-start justify-between">
            <Clamp className="font-subtitle4" clamp={3}>
              {title}
            </Clamp>
            <BookmarkButton
              isSavedCourse={isSavedCourse}
              course={{ originalId: Number(originalId), title }}
              isDark={isDark}
              source={source}
              sourceDetail={sourceDetail}
            />
          </div>
          {teacherName && (
            <Clamp
              className={cn('font-body4 mt-4', {
                'text-base-lighter': isDark,
              })}
              clamp={1}
            >
              {`${t('SEARCH_COURSE_BY')}: ${teacherName}`}
            </Clamp>
          )}
        </div>
        <footer className="flex flex-col w-full mt-16">
          {(me.isAnonymous || me.isPAYG) && !!price.price && price.price > 0 && (
            <p className="flex items-center mb-16">
              <span className="font-currency-xs text-red-main">
                {price.currencySymbol}
              </span>
              <span className="font-price-xs text-red-main">
                {price.price.toLocaleString()}
              </span>
              {price.originalPrice && (
                <span
                  className={cn(
                    'font-discount-xs line-through ml-8',
                    isDark ? 'text-base-lighter' : 'text-gray-dark',
                  )}
                >
                  {price.currencySymbol}
                  {price.originalPrice.toLocaleString()}
                </span>
              )}
            </p>
          )}
          {renderButtons()}
        </footer>
      </div>
    </article>
  );
};

export default SearchCourseCard;
